/**
 * Shape and per-surface visibility of the Plan Actions menu
 * (`planActionMenuItems`).
 *
 * The same menu config is rendered from three places, and each shows a
 * different subset. Rather than branch per key inside the toolbars, each
 * customer config declares the surfaces a key belongs to and
 * `buildPlanActionMenu` drops the rest.
 *
 * The visibility map is keyed by menu key rather than declared on the item
 * objects because `edit_plan` has no item to declare: it is injected at runtime
 * by `applyEditLockToMenu`. Keeping every key in one map is also what lets the
 * test-id manifest publish a locator per surface.
 */
import type { MenuProps } from 'antd';

export type PlanActionMenuItem = NonNullable<MenuProps['items']>[number];

/**
 * The menu as divider-separated sections. Dividers are derived when the menu is
 * flattened, so hiding a whole section on one view cannot leave a rule behind.
 */
export type PlanActionMenuSections = PlanActionMenuItem[][];

/**
 * The one menu key with no `planActionMenuItems` entry: `applyEditLockToMenu`
 * injects the row at runtime. It lives here, with the rest of the menu
 * vocabulary, so the config layer can reason about it without importing from
 * `PlanDetail`; `applyEditLockToMenu` re-exports it for its existing callers.
 */
export const EDIT_PLAN_MENU_KEY = 'edit_plan';

/**
 * Surfaces a plan-action key can render on. Adding one is additive: extend this
 * list, then name it in the configs that should show items there.
 */
export const PLAN_ACTION_SURFACES = ['calendar_original', 'calendar_optimized', 'metric'] as const;

export type PlanActionSurface = typeof PLAN_ACTION_SURFACES[number];

/**
 * Menu key -> surfaces it renders on.
 *
 * An ABSENT key is visible everywhere, so a customer config authored before
 * this field keeps today's behaviour. An EMPTY array hides the item on every
 * surface, which is how an item stays defined (label, key, order) while parked.
 */
export type PlanActionMenuViews = Record<string, PlanActionSurface[]>;
